import {
  ArrowForward as ArrowForwardIcon,
  MenuBook as BookIcon,
  Assessment as ChartIcon,
  Email as EmailIcon,
  NewReleases as NewReleasesIcon,
  Pets as PetsIcon,
  Phone as PhoneIcon,
  QuestionAnswer as QAIcon,
  Settings as SettingsIcon,
  TableChart as TableIcon,
  Translate as TranslateIcon,
} from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material'
import React from 'react'
import { Title, useLocaleState, useTranslate } from 'react-admin'
import { useNavigate } from 'react-router-dom'
import PageHeader from '../components/PageHeader'

const HelpPage: React.FC = () => {
  const translate = useTranslate()
  const navigate = useNavigate()

  // Read the ACTUAL locale. This used to infer it by comparing a TRANSLATED
  // string back to its English source —
  //   translate('pos.language') === 'Language' ? 'en' : 'zh'
  // — which sent every non-English locale down the 'zh' arm, so fr/ja/es users
  // got a correctly translated UI and CHINESE documentation. The docs exist in
  // en and zh only, so anything that is not zh falls back to English.
  const [locale] = useLocaleState()
  const currentLanguage = locale === 'zh' ? 'zh' : 'en'
  const isEnglish = currentLanguage === 'en'

  // Base URL for documentation - uses internal app routes
  const docsBaseUrl = '/docs'

  const helpResources = [
    {
      titleKey: 'pos.help.quick_start',
      descriptionKey: 'pos.help.quick_start_desc',
      icon: <BookIcon />,
      url: `${docsBaseUrl}/${currentLanguage}/overview`,
    },
    {
      titleKey: 'pos.help.features_guide',
      descriptionKey: 'pos.help.features_guide_desc',
      icon: <SettingsIcon />,
      url: `${docsBaseUrl}/${currentLanguage}/features`,
    },
    {
      titleKey: 'pos.help.reading_tables',
      descriptionKey: 'pos.help.reading_tables_desc',
      icon: <TableIcon />,
      url: `${docsBaseUrl}/${currentLanguage}/tables`,
    },
    {
      titleKey: 'pos.help.understanding_charts',
      descriptionKey: 'pos.help.understanding_charts_desc',
      icon: <ChartIcon />,
      url: `${docsBaseUrl}/${currentLanguage}/charts`,
    },
    {
      titleKey: 'pos.help.faq',
      descriptionKey: 'pos.help.faq_desc',
      icon: <QAIcon />,
      url: `${docsBaseUrl}/${currentLanguage}/faq`,
    },
    {
      titleKey: 'pos.whats_new.title',
      descriptionKey: 'pos.whats_new.help_desc',
      icon: <NewReleasesIcon />,
      url: '/whats-new',
    },
  ]

  const supportOptions = [
    {
      titleKey: 'pos.help.support.email',
      descriptionKey: 'pos.help.support.email_desc',
      icon: <EmailIcon />,
      action: 'mailto:support@adapetmedical.com',
    },
    {
      titleKey: 'pos.help.support.phone',
      descriptionKey: 'pos.help.support.phone_desc',
      icon: <PhoneIcon />,
      action: 'tel:+1-888-444-2618',
    },
  ]

  const handleResourceClick = (url: string) => {
    // Navigate to internal documentation page
    navigate(url)
  }

  return (
    <Box>
      <Title title={translate('pos.help.page_title')} />

      {/* The welcome line is the page's subtitle — it used to be an info-blue
          Alert, a banner-shaped "notice" that carried no news. */}
      <PageHeader
        title={translate('pos.help.title')}
        subtitle={translate('pos.help.welcome_message')}
      />

      {/* User Guides Section */}
      <Box component="section" sx={{ mb: 4 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1,
            mb: 1.5,
          }}
        >
          <Box>
            <Typography variant="h6" component="h2">
              {translate('pos.help.user_guides')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {translate('pos.help.user_guides_desc')}
            </Typography>
          </Box>
          {/* Which language the guides open in (docs exist in en + zh). */}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            <TranslateIcon sx={{ fontSize: 16 }} />
            {translate('pos.help.current_language')}:{' '}
            <Box component="span" sx={{ fontWeight: 600 }}>
              {isEnglish ? 'English' : '简体中文'}
            </Box>
          </Typography>
        </Box>

        <Grid container spacing={2}>
          {helpResources.map((resource) => (
            <Grid item xs={12} sm={6} md={4} key={resource.titleKey}>
              {/* CardActionArea, not a clickable div: the cards were
                  unreachable by keyboard. */}
              <Card
                variant="outlined"
                sx={{
                  height: '100%',
                  transition: 'border-color 150ms ease, box-shadow 150ms ease',
                  '&:hover': {
                    borderColor: 'primary.main',
                    boxShadow: '0 4px 12px rgba(16, 24, 40, 0.06)',
                  },
                }}
              >
                <CardActionArea
                  onClick={() => handleResourceClick(resource.url)}
                  sx={{ height: '100%', alignItems: 'flex-start', display: 'flex' }}
                >
                  <CardContent sx={{ width: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1 }}>
                      <Box
                        aria-hidden
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 1.5,
                          display: 'grid',
                          placeItems: 'center',
                          bgcolor: 'rgba(79, 60, 201, 0.08)',
                          color: 'primary.main',
                          '& .MuiSvgIcon-root': { fontSize: 18 },
                        }}
                      >
                        {resource.icon}
                      </Box>
                      <Typography variant="subtitle1" component="h3">
                        {translate(resource.titleKey)}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      {translate(resource.descriptionKey)}
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        color: 'primary.main',
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'inherit' }}>
                        {translate('pos.help.open_guide')}
                      </Typography>
                      <ArrowForwardIcon sx={{ fontSize: 16 }} />
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Grid container spacing={3}>
        {/* Quick Reference Section */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" component="h2" gutterBottom>
                {translate('pos.help.quick_reference')}
              </Typography>
              <List dense disablePadding>
                {[
                  { icon: <BookIcon fontSize="small" />, key: 'navigation' },
                  { icon: <PetsIcon fontSize="small" />, key: 'pet_selection' },
                  { icon: <ChartIcon fontSize="small" />, key: 'glucose_monitoring' },
                ].map(({ icon, key }) => (
                  <ListItem key={key} disableGutters>
                    <ListItemIcon sx={{ minWidth: 36, color: 'text.secondary' }}>
                      {icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={translate(`pos.help.quick_ref.${key}`)}
                      secondary={translate(`pos.help.quick_ref.${key}_desc`)}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Support & Contact. (A "Live Chat Available — use the chat button"
            callout lived here; the chat widget is not mounted — see
            layout/Layout.tsx — so it pointed vets at a button that does not
            exist.) */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" component="h2" gutterBottom>
                {translate('pos.help.support_contact')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {translate('pos.help.support_desc')}
              </Typography>
              <List dense disablePadding>
                {supportOptions.map((option) => (
                  <ListItem
                    key={option.titleKey}
                    disableGutters
                    secondaryAction={
                      // A real mailto:/tel: link. window.open(…, '_blank') left
                      // an empty tab behind in desktop browsers.
                      <Button size="small" variant="outlined" component="a" href={option.action}>
                        {translate('pos.help.contact')}
                      </Button>
                    }
                    sx={{ pr: 12 }}
                  >
                    <ListItemIcon sx={{ minWidth: 36, color: 'text.secondary' }}>
                      {option.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={translate(option.titleKey)}
                      secondary={translate(option.descriptionKey)}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Emergency Notice — a clinical-safety disclaimer, so it keeps the
          attention tone. */}
      <Alert severity="warning" sx={{ mt: 3 }}>
        <Typography variant="body2">
          <strong>{translate('pos.help.emergency_notice_title')}</strong>
          <br />
          {translate('pos.help.emergency_notice_desc')}
        </Typography>
      </Alert>
    </Box>
  )
}

export default HelpPage
